import React, { useEffect, useState, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import LaunchIcon from "@mui/icons-material/Launch";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { ORGANIZATION_STRUCTURE_FIELDS } from "../../../contants/formFields";
// import CustomAutoComplete from "../../../../components/CustomAutoComplete";
import IntroductionFormApi from "../../../services/api/introductionForm";
// import Button from "../Button";
import ZoneLocator from "./ZoneLocator/ZoneLocator";
import Styles from "./OrganizationStructure.styles";
// import AutoCompleteDropdown from "../AutoCompleteDropdown/AutoCompleteDropdown";
import AutoCompleteDropdown from "../new/AutoCompleteDropdown/AutoCompleteDropdown";
import { getOptions } from "../../../utils";
import { SET_ORGANISATION_STRUCTURE } from "../../../redux/actionTypes/meta";
import { isEmptyObj } from "../../../utils/helper";
import { OS_REDUX_KEY_MAPPER } from "../../../contants";
import Colors from "../../../theme/colors";

function OrganizationStructureRegion({
  values,
  errors,
  setFieldValue,
  showZoneLocator,
  showBlock,
  containerStyle,
  showTitle,
  required,
  useHiddenInput,
  storeValuesInRedux,
  shouldFetchOtherOS,
  disabledOSKeys,
  hiddenFields,
  hideDistrict,
}) {
  const [areaData, setAreadData] = useState([]);
  const [zoneData, setZoneData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [chapterData, setChapterData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [isZoneLocatorOpen, setIsZoneLocatorOpen] = useState(false);
  const formRequestRef = useRef(true);
  const dispatch = useDispatch();

  const getData = async (api, setState, params, setter) => {
    try {
      let response;
      if (params) {
        response = await api({ data: params });
      } else {
        response = await api({});
      }
      setState(response?.data || response || []);
      if (setter) {
        setter();
      }
      if (storeValuesInRedux) {
        const key = isEmptyObj(params)
          ? "areas"
          : OS_REDUX_KEY_MAPPER[Object.keys(params)[0]];

        dispatch({
          type: SET_ORGANISATION_STRUCTURE,
          payload: {
            [key]: response?.data,
          },
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const openZoneLocator = () => {
    setIsZoneLocatorOpen(true);
  };

  const handleZoneLocationSelection = (selectedZoneData) => {
    setRegionData([]);
    setChapterData([]);
    setDistrictData([]);
    setBlockData([]);
    setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.REGION, "");
    setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER, "");
    setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
    setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");

    setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.AREA, selectedZoneData.areaId);

    if (selectedZoneData.areaId) {
      getData(
        IntroductionFormApi.getAllZones,
        setZoneData,
        {
          areaId: selectedZoneData.areaId,
        },
        () =>
          setFieldValue(
            ORGANIZATION_STRUCTURE_FIELDS.ZONE,
            selectedZoneData.zoneId
          )
      );
    }

    if (selectedZoneData.zoneId) {
      getData(IntroductionFormApi.getAllRegions, setRegionData, {
        zoneId: selectedZoneData.zoneId,
      });
    }

    setIsZoneLocatorOpen(false);
  };

  const fetchAllOtherOS = () => {
    if (
      values.areaId &&
      !hiddenFields.includes(ORGANIZATION_STRUCTURE_FIELDS.ZONE)
    ) {
      getData(IntroductionFormApi.getAllZones, setZoneData, {
        areaId: values.areaId,
      });
    }
    if (
      values.zoneId &&
      !hiddenFields.includes(ORGANIZATION_STRUCTURE_FIELDS.REGION)
    ) {
      getData(IntroductionFormApi.getAllRegions, setRegionData, {
        zoneId: values.zoneId,
      });
    }
    if (
      values.regionId &&
      !hiddenFields.includes(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER)
    ) {
      getData(IntroductionFormApi.getAllChapters, setChapterData, {
        regionId: values.regionId,
      });
    }
    if (values.chapterId) {
      getData(IntroductionFormApi.getAllDistricts, setDistrictData, {
        chapterId: values.chapterId,
      });
    }
    if (values.districtId) {
      getData(IntroductionFormApi.getAllBlocks, setBlockData, {
        districtId: values.districtId,
      });
    }
  };

  const getHideStyle = (field) =>
    hiddenFields.includes(field) ? { display: "none" } : {};

  useEffect(() => {
    getData(IntroductionFormApi.getAllArea, setAreadData);
    fetchAllOtherOS();
  }, []);

  useEffect(() => {
    if (shouldFetchOtherOS && formRequestRef.current) {
      fetchAllOtherOS();
      formRequestRef.current = false;
    }
  }, [values, shouldFetchOtherOS]);

  return (
    <>
      {showTitle && (
        <Typography style={Styles.typography}>
          Organization Structure
        </Typography>
      )}
      <Grid
        container
        style={{
          border: "1px solid #C4C4C4",
          padding: 20,
          ...(containerStyle ?? {}),
        }}
      >
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            label="Area"
            name={ORGANIZATION_STRUCTURE_FIELDS.AREA}
            options={getOptions(areaData, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.AREA]}
            value={values[ORGANIZATION_STRUCTURE_FIELDS.AREA]}
            inputProps={{ required }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.AREA, "");
              } else {
                setFieldValue(
                  ORGANIZATION_STRUCTURE_FIELDS.AREA,
                  selectedValue.value
                );
                getData(IntroductionFormApi.getAllZones, setZoneData, {
                  areaId: selectedValue.value,
                });
              }
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.ZONE, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.REGION, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
              setZoneData([]);
              setRegionData([]);
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
            disabled={disabledOSKeys.includes(
              ORGANIZATION_STRUCTURE_FIELDS.AREA
            )}
          />
        </Grid>

        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            label="Zone"
            name={ORGANIZATION_STRUCTURE_FIELDS.ZONE}
            options={getOptions(zoneData, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.ZONE]}
            value={values[ORGANIZATION_STRUCTURE_FIELDS.ZONE]}
            disabled={
              !values[ORGANIZATION_STRUCTURE_FIELDS.AREA] ||
              disabledOSKeys.includes(ORGANIZATION_STRUCTURE_FIELDS.ZONE)
            }
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.ZONE, "");
              } else {
                setFieldValue(
                  ORGANIZATION_STRUCTURE_FIELDS.ZONE,
                  selectedValue.value
                );
                getData(IntroductionFormApi.getAllRegions, setRegionData, {
                  zoneId: selectedValue.value,
                });
              }
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.REGION, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
              setRegionData([]);
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            label="Region"
            name={ORGANIZATION_STRUCTURE_FIELDS.REGION}
            options={getOptions(regionData, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.REGION]}
            disabled={
              !values[ORGANIZATION_STRUCTURE_FIELDS.ZONE] ||
              disabledOSKeys.includes(ORGANIZATION_STRUCTURE_FIELDS.REGION)
            }
            value={values[ORGANIZATION_STRUCTURE_FIELDS.REGION]}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.REGION, "");
              } else {
                setFieldValue(
                  ORGANIZATION_STRUCTURE_FIELDS.REGION,
                  selectedValue.value
                );
                getData(IntroductionFormApi.getAllChapters, setChapterData, {
                  regionId: selectedValue.value,
                });
              }
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER, "");
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            label="Chapter"
            name={ORGANIZATION_STRUCTURE_FIELDS.CHAPTER}
            options={getOptions(chapterData, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]}
            value={values[ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]}
            // disabled={
            //   !values[ORGANIZATION_STRUCTURE_FIELDS.REGION] ||
            //   disabledOSKeys.includes(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER)
            // }
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER, "");
              } else {
                setFieldValue(
                  ORGANIZATION_STRUCTURE_FIELDS.CHAPTER,
                  selectedValue.value
                );
                getData(IntroductionFormApi.getAllDistricts, setDistrictData, {
                  chapterId: selectedValue.value,
                });
              }
              setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
              setDistrictData([]);
              setBlockData([]);
            }}
            style={{ ...getHideStyle(ORGANIZATION_STRUCTURE_FIELDS.CHAPTER) }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        {!hideDistrict ? (
          <Grid item md={12} xs={12} style={Styles.gridStyle}>
            <AutoCompleteDropdown
              getOptionLabel={(option) => option.name}
              label="District"
              name={ORGANIZATION_STRUCTURE_FIELDS.DISTRICT}
              options={getOptions(districtData, "structureId")}
              error={errors[ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]}
              value={values[ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]}
              // disabled={
              //   !values[ORGANIZATION_STRUCTURE_FIELDS.CHAPTER] ||
              //   disabledOSKeys.includes(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT)
              // }
              onChange={(selectedValue) => {
                if (!selectedValue) {
                  setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT, "");
                } else {
                  setFieldValue(
                    ORGANIZATION_STRUCTURE_FIELDS.DISTRICT,
                    selectedValue.value
                  );
                  if (showBlock) {
                    getData(IntroductionFormApi.getAllBlocks, setBlockData, {
                      districtId: selectedValue.value,
                    });
                  }
                }
                setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");
                setBlockData([]);
              }}
              useHiddenInput={useHiddenInput}
              style={{
                ...getHideStyle(ORGANIZATION_STRUCTURE_FIELDS.DISTRICT),
              }}
            />
          </Grid>
        ) : null}
        {showBlock ? (
          <Grid item md={12} xs={12} style={Styles.gridStyle}>
            <AutoCompleteDropdown
              getOptionLabel={(option) => option.name}
              label={
                <Box component="span">
                  Block{" "}
                  {required && (
                    <Typography component="span" color="error">
                      *
                    </Typography>
                  )}
                </Box>
              }
              name={ORGANIZATION_STRUCTURE_FIELDS.BLOCK}
              options={getOptions(blockData, "structureId")}
              error={errors[ORGANIZATION_STRUCTURE_FIELDS.BLOCK]}
              value={values[ORGANIZATION_STRUCTURE_FIELDS.BLOCK]}
              // disabled={
              //   !values[ORGANIZATION_STRUCTURE_FIELDS.DISTRICT] ||
              //   disabledOSKeys.includes(ORGANIZATION_STRUCTURE_FIELDS.BLOCK)
              // }
              onChange={(selectedValue) => {
                if (!selectedValue) {
                  setFieldValue(ORGANIZATION_STRUCTURE_FIELDS.BLOCK, "");
                } else {
                  setFieldValue(
                    ORGANIZATION_STRUCTURE_FIELDS.BLOCK,
                    selectedValue.value
                  );
                }
              }}
              useHiddenInput={useHiddenInput}
            />
          </Grid>
        ) : null}
      </Grid>
      {showZoneLocator && (
        <Box
          sx={{
            borderBottom: "2px solid #003E99",
            width: "46%",
            "&:hover": { borderBottom: "2px solid #0054CC" },
          }}
          onClick={openZoneLocator}
        >
          <Typography
            sx={{
              color: Colors.midnightBlue,
              "&:hover": {
                color: Colors.hoverBlue,
              },
              ...Styles.zoneLocatorContainer,

              alignItems: "center",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            Zone Locator
            <LaunchIcon
              sx={{
                color: Colors.midnightBlue,
                "&:hover": {
                  color: Colors.hoverBlue,
                },
                marginLeft: 5,
                fontSize: 20,
              }}
            />
          </Typography>
        </Box>
      )}
      {isZoneLocatorOpen ? (
        <ZoneLocator
          handleClose={() => {
            setIsZoneLocatorOpen(false);
          }}
          handleSelectedOption={handleZoneLocationSelection}
        />
      ) : null}
    </>
  );
}

const propTypes = {
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  showZoneLocator: PropTypes.bool,
  showBlock: PropTypes.bool,
  containerStyle: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  showTitle: PropTypes.bool,
  required: PropTypes.bool,
  useHiddenInput: PropTypes.bool,
  storeValuesInRedux: PropTypes.bool,
  shouldFetchOtherOS: PropTypes.bool,
  disabledOSKeys: PropTypes.arrayOf(PropTypes.string),
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  hideDistrict: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
};

const defaultProps = {
  showZoneLocator: true,
  showBlock: false,
  containerStyle: null,
  showTitle: true,
  required: true,
  useHiddenInput: false,
  storeValuesInRedux: false,
  shouldFetchOtherOS: false,
  disabledOSKeys: [],
  hiddenFields: [],
  hideDistrict: false,
};

OrganizationStructureRegion.propTypes = propTypes;
OrganizationStructureRegion.defaultProps = defaultProps;

export default OrganizationStructureRegion;
